<template>
  <div class="form-modals">
    <div class="vx-row">
      <div class="vx-col w-full">
        <vx-card title="Form Modals" subtitle="Modal forms and popups used throughout the application">
          
          <!-- Modal Examples -->
          <div class="space-y-8">
            
            <!-- Basic Modal Form -->
            <div class="modal-example">
              <h3 class="text-lg font-semibold mb-4">Basic Modal Form</h3>
              <vs-button @click="showBasicModal = true" color="primary">Open Basic Modal</vs-button>
              
              <vs-popup title="Basic Form Modal" :active.sync="showBasicModal">
                <div class="p-4">
                  <vs-input
                    v-model="basicForm.name"
                    placeholder="Enter your name"
                    label="Name"
                    class="w-full mb-4">
                  </vs-input>
                  
                  <vs-input
                    v-model="basicForm.email"
                    placeholder="Enter your email"
                    label="Email"
                    type="email"
                    class="w-full mb-4">
                  </vs-input>
                  
                  <vs-textarea
                    v-model="basicForm.message"
                    placeholder="Enter your message"
                    label="Message"
                    rows="4"
                    class="w-full mb-4">
                  </vs-textarea>
                  
                  <div class="flex justify-end space-x-2">
                    <vs-button @click="showBasicModal = false" color="warning" type="border">Cancel</vs-button>
                    <vs-button @click="submitBasicForm" color="primary">Submit</vs-button>
                  </div>
                </div>
              </vs-popup>
            </div>

            <!-- Prompt Modal -->
            <div class="modal-example">
              <h3 class="text-lg font-semibold mb-4">Prompt Modal</h3>
              <vs-button @click="showPromptModal = true" color="success">Open Prompt Modal</vs-button>
              
              <vs-prompt
                vs-title="Add New Item"
                vs-accept-text="Add Item"
                vs-cancel-text="Cancel"
                vs-button-cancel="border"
                @vs-accept="submitPromptForm"
                @vs-cancel="showPromptModal = false"
                :vs-is-valid="isPromptValid"
                :vs-active.sync="showPromptModal">
                
                <vs-input
                  v-model="promptForm.title"
                  placeholder="Enter title"
                  label="Title"
                  class="w-full mb-4">
                </vs-input>
                
                <vs-input
                  v-model="promptForm.description"
                  placeholder="Enter description"
                  label="Description"
                  class="w-full mb-4">
                </vs-input>
                
              </vs-prompt>
            </div>

            <!-- Fullscreen Modal -->
            <div class="modal-example">
              <h3 class="text-lg font-semibold mb-4">Fullscreen Modal</h3>
              <vs-button @click="showFullscreenModal = true" color="dark">Open Fullscreen Modal</vs-button>
              
              <vs-popup title="Fullscreen Form" fullscreen :active.sync="showFullscreenModal">
                <div class="p-6">
                  <div class="max-w-2xl mx-auto">
                    <h2 class="text-2xl font-bold mb-6">Complete Form</h2>
                    
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <vs-input
                        v-model="fullscreenForm.firstName"
                        placeholder="First Name"
                        label="First Name"
                        class="w-full">
                      </vs-input>
                      
                      <vs-input
                        v-model="fullscreenForm.lastName"
                        placeholder="Last Name"
                        label="Last Name"
                        class="w-full">
                      </vs-input>
                    </div>
                    
                    <vs-input
                      v-model="fullscreenForm.email"
                      placeholder="Email"
                      label="Email"
                      type="email"
                      class="w-full mt-4">
                    </vs-input>
                    
                    <vs-input
                      v-model="fullscreenForm.phone"
                      placeholder="Phone"
                      label="Phone"
                      class="w-full mt-4">
                    </vs-input>
                    
                    <vs-textarea
                      v-model="fullscreenForm.address"
                      placeholder="Address"
                      label="Address"
                      rows="3"
                      class="w-full mt-4">
                    </vs-textarea>
                    
                    <div class="flex justify-end space-x-2 mt-6">
                      <vs-button @click="showFullscreenModal = false" color="warning" type="border">Cancel</vs-button>
                      <vs-button @click="submitFullscreenForm" color="primary">Save</vs-button>
                    </div>
                  </div>
                </div>
              </vs-popup>
            </div>

            <!-- Modal with Validation -->
            <div class="modal-example">
              <h3 class="text-lg font-semibold mb-4">Modal with Validation</h3>
              <vs-button @click="showValidationModal = true" color="warning">Open Validation Modal</vs-button>
              
              <vs-popup title="Form with Validation" :active.sync="showValidationModal">
                <div class="p-4">
                  <form @submit.prevent="submitValidationForm">
                    <vs-input
                      v-model="validationForm.name"
                      placeholder="Enter your name"
                      label="Name"
                      :danger="validationErrors.name"
                      :danger-text="validationErrors.name"
                      class="w-full mb-4">
                    </vs-input>
                    
                    <vs-input
                      v-model="validationForm.email"
                      placeholder="Enter your email"
                      label="Email"
                      type="email"
                      :danger="validationErrors.email"
                      :danger-text="validationErrors.email"
                      class="w-full mb-4">
                    </vs-input>
                    
                    <vs-input
                      v-model="validationForm.password"
                      placeholder="Enter password"
                      label="Password"
                      type="password"
                      :danger="validationErrors.password"
                      :danger-text="validationErrors.password"
                      class="w-full mb-4">
                    </vs-input>
                    
                    <vs-input
                      v-model="validationForm.confirmPassword"
                      placeholder="Confirm password"
                      label="Confirm Password"
                      type="password"
                      :danger="validationErrors.confirmPassword"
                      :danger-text="validationErrors.confirmPassword"
                      class="w-full mb-4">
                    </vs-input>
                    
                    <div class="flex justify-end space-x-2">
                      <vs-button @click="showValidationModal = false" color="warning" type="border">Cancel</vs-button>
                      <vs-button type="submit" color="primary">Submit</vs-button>
                    </div>
                  </form>
                </div>
              </vs-popup>
            </div>

          </div>

        </vx-card>
      </div>
    </div>
  </div>
</template>

<script>
import VxCard from '@/components/vx-card/VxCard.vue'

export default {
  name: 'FormModals',
  components: {
    VxCard
  },
  data() {
    return {
      showBasicModal: false,
      showPromptModal: false,
      showFullscreenModal: false,
      showValidationModal: false,
      
      basicForm: {
        name: '',
        email: '',
        message: ''
      },
      
      promptForm: {
        title: '',
        description: ''
      },
      
      fullscreenForm: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: ''
      },
      
      validationForm: {
        name: '',
        email: '',
        password: '',
        confirmPassword: ''
      },
      
      validationErrors: {
        name: '',
        email: '',
        password: '',
        confirmPassword: ''
      }
    }
  },
  computed: {
    isPromptValid() {
      return this.promptForm.title && this.promptForm.description
    }
  },
  methods: {
    submitBasicForm() {
      console.log('Basic form submitted:', this.basicForm)
      this.showBasicModal = false
      this.$vs.notify({
        title: 'Success',
        text: 'Basic form submitted successfully',
        color: 'success'
      })
    },
    
    submitPromptForm() {
      console.log('Prompt form submitted:', this.promptForm)
      this.$vs.notify({
        title: 'Success',
        text: 'Prompt form submitted successfully',
        color: 'success'
      })
    },
    
    submitFullscreenForm() {
      console.log('Fullscreen form submitted:', this.fullscreenForm)
      this.showFullscreenModal = false
      this.$vs.notify({
        title: 'Success',
        text: 'Fullscreen form submitted successfully',
        color: 'success'
      })
    },
    
    submitValidationForm() {
      this.validateForm()
      
      if (this.isFormValid()) {
        console.log('Validation form submitted:', this.validationForm)
        this.showValidationModal = false
        this.$vs.notify({
          title: 'Success',
          text: 'Validation form submitted successfully',
          color: 'success'
        })
      } else {
        this.$vs.notify({
          title: 'Validation Error',
          text: 'Please fix the errors and try again',
          color: 'danger'
        })
      }
    },
    
    validateForm() {
      this.validationErrors = {
        name: '',
        email: '',
        password: '',
        confirmPassword: ''
      }
      
      if (!this.validationForm.name) {
        this.validationErrors.name = 'Name is required'
      }
      
      if (!this.validationForm.email) {
        this.validationErrors.email = 'Email is required'
      } else if (!this.isValidEmail(this.validationForm.email)) {
        this.validationErrors.email = 'Please enter a valid email'
      }
      
      if (!this.validationForm.password) {
        this.validationErrors.password = 'Password is required'
      } else if (this.validationForm.password.length < 6) {
        this.validationErrors.password = 'Password must be at least 6 characters'
      }
      
      if (this.validationForm.password !== this.validationForm.confirmPassword) {
        this.validationErrors.confirmPassword = 'Passwords do not match'
      }
    },
    
    isFormValid() {
      return !Object.values(this.validationErrors).some(error => error !== '')
    },
    
    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(email)
    }
  }
}
</script>

<style scoped>
.modal-example {
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 1.5rem;
  background-color: #f9fafb;
}
</style>
